import { FC, useState, useCallback, useContext, useMemo } from "react"
import { utils, FieldProps } from "@rjsf/core"
import type { JSONSchema7 } from "json-schema"
import { Text, Title, ValidationError } from "dash-styled"
import { ToolsContext } from "resha"

export const MultiSchemaField: FC<FieldProps> = ({
    formData,
    options,
    registry,
    idSchema,
    onChange,
    baseType,
    disabled,
    errorSchema,
    idPrefix,
    onBlur,
    onFocus,
    uiSchema,
    required,
    readonly,
    autofocus,
    formContext,
    schema,
    name
}) => {
    const toolsContext = useContext(ToolsContext)
    const skipTitleToolsContext = useMemo(
        () => ({ ...toolsContext, skipMultiSchemaTitle: true }),
        [toolsContext]
    )
    const [selectedOption, setSelectedOption] = useState(
        formData
            ? utils.getMatchingOption(formData, options, registry.rootSchema)
            : -1
    )

    const onOptionChange = useCallback(
        option => {
            const selectedOption = parseInt(option, 10)
            const { rootSchema } = registry
            const newOption = utils.retrieveSchema(
                options[selectedOption],
                rootSchema,
                formData
            )

            // If the new option is of type object and the current data is an object,
            // discard properties added using the old option.
            let newFormData = undefined
            if (
                utils.guessType(formData) === "object" &&
                (newOption.type === "object" || newOption.properties)
            ) {
                newFormData = Object.assign({}, formData)

                const optionsToDiscard = options.slice()
                optionsToDiscard.splice(selectedOption, 1)

                // Discard any data added using other options
                for (const option of optionsToDiscard) {
                    if (option.properties) {
                        for (const key in option.properties) {
                            if (newFormData.hasOwnProperty(key)) {
                                delete newFormData[key]
                            }
                        }
                    }
                }
            }

            // Call getDefaultFormState to make sure defaults are populated on change.
            onChange(
                utils.getDefaultFormState(
                    options[selectedOption],
                    newFormData,
                    rootSchema
                )
            )

            setSelectedOption(parseInt(option, 10))
        },
        [options, onChange, registry, formData]
    )

    const SchemaField = registry.fields.SchemaField
    const { widgets } = registry

    const uiOptions = utils.getUiOptions(uiSchema)
    const Widget = utils.getWidget(
        { type: "number" },
        (uiOptions?.widget ?? "select") as string,
        widgets
    )

    const option = options[selectedOption] ?? null
    let optionSchema

    if (option) {
        // If the subschema doesn't declare a type, infer the type from the
        // parent schema
        optionSchema = option.type
            ? option
            : Object.assign({}, option, { type: baseType })
    }

    const enumOptions = options.map((option: JSONSchema7, index: number) => ({
        label: option.title || `Option ${index + 1}`,
        value: index
    }))

    const idName = useMemo(
        () => (schema.oneOf !== undefined ? "oneof" : "anyof"),
        [schema]
    )

    const selectSchema = useMemo<JSONSchema7>(
        () => ({ type: "number", default: 0 }),
        []
    )
    return (
        <ToolsContext.Provider value={skipTitleToolsContext}>
            <div>
                <div className="flex items-center justify-between mb-3">
                    <Text>{schema.title ? `${schema.title}*` : ""}</Text>
                    <div className="w-2/3">
                        <Widget
                            id={`${idSchema.$id}__${idName}_select`}
                            schema={{
                                ...selectSchema,
                                title: ""
                            }}
                            onChange={onOptionChange}
                            onBlur={onBlur}
                            onFocus={onFocus}
                            value={selectedOption}
                            options={{ enumOptions }}
                            uiSchema={{}}
                            label=""
                            required={required}
                            disabled={disabled}
                            readonly={readonly}
                            autofocus={autofocus}
                            placeholder=""
                            formContext={formContext}
                            multiple={false}
                            rawErrors={[]}
                            registry={registry}
                            {...uiOptions}
                        />
                        {!option && errorSchema?.__errors?.[0] && (
                            <ValidationError className="mr-1">
                                {errorSchema?.__errors?.[0]}
                            </ValidationError>
                        )}
                    </div>
                </div>
                {option !== null && (
                    <>
                        {option?.title && (
                            <Title
                                component="h3"
                                variant="heading3"
                                weight="semibold"
                            >
                                {option.title}
                            </Title>
                        )}
                        <div>
                            <SchemaField
                                formContext={formContext}
                                autofocus={autofocus}
                                readonly={readonly}
                                required={required}
                                name={name}
                                schema={optionSchema}
                                uiSchema={uiSchema}
                                errorSchema={{
                                    ...errorSchema,
                                    type: {}
                                }}
                                idSchema={idSchema}
                                idPrefix={idPrefix}
                                formData={formData}
                                onChange={onChange}
                                onBlur={onBlur}
                                onFocus={onFocus}
                                registry={registry}
                                disabled={disabled}
                            />
                        </div>
                    </>
                )}
            </div>
        </ToolsContext.Provider>
    )
}
